import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const CategoryManagement = ({ categories, tenantId, isGymOwner, isStaff, onClose, onSaved }) => {
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleAddCategory = async (e) => {
    e?.preventDefault();
    if (!newCategory?.name?.trim()) {
      setError('Category name is required');
      return;
    }

    if (!tenantId) {
      setError('Tenant ID is missing. Please refresh and try again.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const { data, error: insertError } = await supabase
        ?.from('pos_categories')
        ?.insert([{
          tenant_id: tenantId,
          name: newCategory?.name?.trim(),
          description: newCategory?.description?.trim() || null,
          display_order: categories?.length || 0
        }])
        ?.select();

      if (insertError) {
        console.error('Insert category error:', insertError);
        // Provide more specific error messages
        if (insertError?.code === '23505') {
          setError('A category with this name already exists');
        } else if (insertError?.code === '42501') {
          setError('You do not have permission to add categories. Please contact your administrator.');
        } else if (insertError?.message) {
          setError(insertError?.message);
        } else {
          setError('Failed to add category. Please try again.');
        }
        return;
      }

      setNewCategory({ name: '', description: '' });
      setError(null);
      onSaved?.();
    } catch (error) {
      console.error('Add category error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure? Products in this category will be uncategorized.')) return;

    try {
      const { error } = await supabase
        ?.from('pos_categories')
        ?.update({ is_active: false })
        ?.eq('id', categoryId);

      if (error) throw error;
      onSaved?.();
    } catch (error) {
      console.error('Delete category error:', error);
      alert('Failed to delete category');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">Manage Categories</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Category */}
          <form onSubmit={handleAddCategory} className="mb-6 p-4 bg-background rounded-lg border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">Add New Category</h3>
            
            {/* Error Message */}
            {error && (
              <div className="mb-3 p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-start gap-2">
                <Icon name="AlertCircle" size={18} className="text-danger mt-0.5 flex-shrink-0" />
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            <div className="space-y-3">
              <input
                type="text"
                value={newCategory?.name}
                onChange={(e) => {
                  setNewCategory({ ...newCategory, name: e?.target?.value });
                  setError(null);
                }}
                placeholder="Category name"
                className="w-full px-4 py-2 rounded-lg bg-card border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
              <input
                type="text"
                value={newCategory?.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e?.target?.value })}
                placeholder="Description (optional)"
                className="w-full px-4 py-2 rounded-lg bg-card border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
              <Button 
                type="submit" 
                disabled={saving || !newCategory?.name?.trim()} 
                className="w-full"
              >
                {saving ? (
                  <>
                    <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Icon name="Plus" size={18} className="mr-2" />
                    Add Category
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Existing Categories */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Existing Categories</h3>
            {categories?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No categories yet. Add your first category above.
              </p>
            ) : (
              <div className="space-y-2">
                {categories?.map((category) => (
                  <div
                    key={category?.id}
                    className="flex items-center justify-between p-4 bg-background rounded-lg border border-border"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{category?.name}</h4>
                      {category?.description && (
                        <p className="text-sm text-muted-foreground mt-1">{category?.description}</p>
                      )}
                    </div>
                    {(isGymOwner || isStaff) && (
                      <button
                        onClick={() => handleDeleteCategory(category?.id)}
                        className="ml-4 text-danger hover:bg-danger/10 p-2 rounded transition-smooth"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <Button variant="ghost" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;